/**
 * Who is signed in, and the token that says so.
 *
 * The seam is deliberately the same shape as `admin-api`'s `TokenVerifier` and
 * `apps/app`'s `AuthProvider`: two implementations, one interface, and nothing
 * downstream knows which it has. There it is "verify a signature or do not";
 * here it is "ask Firebase for a token or mint the unsigned dev shape".
 *
 * One thing is different from the staff app, and it is the whole of this
 * surface's identity model: **an operator is not a tenant's anything.** The
 * token carries `operator: true` and no `tenant_id`, and `admin-api` refuses a
 * token that carries both — a credential whose reach depended on which service
 * it was pointed at would be a staff device quietly given the fleet. So there
 * is no tenant in this interface, nowhere to put one, and no build-time
 * `VITE_AUTH_FAKE_TENANT` to mirror `apps/app`'s.
 */

export interface OperatorUser {
  uid: string;
  email: string | null;
}

export interface AuthProvider {
  readonly kind: 'firebase' | 'fake';
  /** Null until the first `onChange`, then the signed-in operator or null. */
  current(): OperatorUser | null;
  signIn(email: string, password: string): Promise<void>;
  signOut(): Promise<void>;
  /**
   * The ID token, refreshed when asked. Returns null when nobody is signed in —
   * the client sends the request anyway and lets `admin-api`'s `401` be the
   * authority on it, rather than this app inventing a second opinion about
   * whether a credential is good.
   */
  token(forceRefresh?: boolean): Promise<string | null>;
  onChange(listener: (user: OperatorUser | null) => void): () => void;
}

export class AuthError extends Error {
  readonly reason: 'bad_credentials' | 'unavailable';
  constructor(reason: 'bad_credentials' | 'unavailable', message: string) {
    super(message);
    this.name = 'AuthError';
    this.reason = reason;
  }
}
